"use client";

import {
  ParcelasTable,
  PixSection,
  usePrenotaStore,
  useParcelas,
} from "@PN/inclusao";
import { Banknote } from "lucide-react";
import { useCatalogs } from "store";
import { useMemo, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Combobox,
  Separator,
  cn,
  Loader,
  parse,
} from "visuais";
import { ParcelasRulesDialog } from "./ParcelasRulesDialog";
import { validateAllParcelas } from "./rules/parcelasRules";

export function CondicaoDialog({ className }: { className?: string }) {
  const pagamentos = usePrenotaStore((state) => state.draft.pagamentos);
  const aux = usePrenotaStore((state) => state.aux);
  const setPagamentos = usePrenotaStore(
    (state) => state.actions.draft.setPagamentos
  );

  // ✅ Verifica se há pedido de compra vinculado (mesma lógica do useCardFlow)
  const temPedidoCompra = useMemo(() => {
    return !!(
      aux.pedidos?.length &&
      (aux.pedidos[0]?.codPedido ?? "").trim() !== ""
    );
  }, [aux.pedidos]);

  // Hook para parcelas (inclui busca automática)
  const {
    parcelas,
    valorNF,
    isLoading,
    canFetch: canFetchParcelas,
    handleForceRefetch,
    parcelasBaseProtheus,
    parcelasValorBaseProtheus,
    parcelasValorBaseProtheusNormalizado,
    parcelasValorBaseProtheusPorIndice,
    restoreParcelasFromProtheusBase,
  } = useParcelas();

  const { condicoes } = useCatalogs();
  
  const condicoesFiltradas = useMemo(() => {
    return condicoes.options.map((opt) => ({
      value: opt.value,
      label: `${opt.value} - ${opt.label}`,
    }));
  }, [condicoes.options]);
  const condicoesTipo9Filtradas = useMemo(() => {
    return condicoes.options
      .filter((opt) => condicoes.getBy("E4_CODIGO", opt.value)?.E4_TIPO === "9")
      .map((opt) => ({
        value: opt.value,
        label: `${opt.value} - ${opt.label}`,
      }));
  }, [condicoes.options, condicoes]);

  const condicaoRow = useMemo(
    () => condicoes.getBy("E4_CODIGO", pagamentos.condicaoFinanceira),
    [pagamentos.condicaoFinanceira, condicoes]
  );

  const isTipoLivre = condicaoRow?.E4_TIPO === "9";
  const canShowAlterarParcelas = useMemo(
    () => temPedidoCompra && !!condicaoRow && condicaoRow.E4_TIPO !== "9",
    [temPedidoCompra, condicaoRow]
  );

  const condicaoDescricao = useMemo(() => {
    return condicaoRow
      ? `${condicaoRow.E4_CODIGO} - ${condicaoRow.E4_DESCRI.trim()}`
      : "Não selecionada";
  }, [condicaoRow]);

  // Estado para controlar se o dialog está aberto
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);
  const [alterarParcelas, setAlterarParcelas] = useState(
    !!pagamentos.condicaoNova?.trim()
  );
  
  // Estado para rastrear a condição anterior
  const [condicaoAnterior, setCondicaoAnterior] = useState<string | undefined>(
    pagamentos.condicaoFinanceira
  );
  useEffect(() => {
    setAlterarParcelas(!!pagamentos.condicaoNova?.trim());
  }, [pagamentos.condicaoNova]);
  useEffect(() => {
    setHasMounted(true);
  }, []);

  // ✅ Busca automaticamente quando a condição de pagamento mudar
  useEffect(() => {
    const condicaoAtual = pagamentos.condicaoFinanceira;
    
    // Se a condição mudou e pode buscar, busca automaticamente
    if (
      condicaoAtual &&
      condicaoAtual !== condicaoAnterior &&
      condicaoAnterior !== undefined && // Evita buscar na primeira renderização
      canFetchParcelas
    ) {
      console.log(
        "💰 [CondicaoDialog] Condição de pagamento mudou - buscando parcelas automaticamente",
        { de: condicaoAnterior, para: condicaoAtual }
      );
      handleForceRefetch();
    }
    
    // Atualiza a condição anterior
    setCondicaoAnterior(condicaoAtual);
  }, [
    pagamentos.condicaoFinanceira,
    condicaoAnterior,
    canFetchParcelas,
    handleForceRefetch,
  ]);

  // Trigger label dinâmico com status da busca automática
  const btnLabel = useMemo(() => {
    if (isLoading && canFetchParcelas) {
      return "Buscando parcelas...";
    }

    if (condicaoDescricao && condicaoDescricao !== "Não selecionada") {
      // Mostra todas as datas das parcelas - o trigger controla o truncate
      const datasInfo =
        parcelas.length > 0
          ? parcelas
              .map((p) => {
                if (!p.vencimento) return "";

                // ✅ CORRIGIDO: Usa parse consistente para evitar problemas de timezone
                const date = parse.dates.ISO(p.vencimento, { allowNull: true });
                if (!date) return "";

                return date.toLocaleDateString("pt-BR", {
                  day: "2-digit",
                  month: "2-digit",
                });
              })
              .filter(Boolean)
              .join(", ")
          : "";

      const datasText = datasInfo ? ` • (${datasInfo})` : "";

      return `${condicaoDescricao}${datasText}`;
    }

    return "Configurar financeiro";
  }, [
    condicaoDescricao,
    isLoading,
    canFetchParcelas,
    parcelas,
  ]);

  const handleCondicaoChange = (value: string | undefined) => {
    if (value) {
      setPagamentos({
        ...pagamentos,
        condicaoFinanceira: value,
      });
    }
  };
  const handleAlterarParcelasChange = (checked: boolean) => {
    setAlterarParcelas(checked);
    if (!checked) {
      restoreParcelasFromProtheusBase({ condicaoNova: "" });
    }
  };
  const handleCondicaoNovaChange = (value: string | undefined) => {
    setPagamentos({
      ...pagamentos,
      condicaoNova: value || "",
    });
  };

  const condicaoNovaPendente = useMemo(
    () => alterarParcelas && !pagamentos.condicaoNova?.trim(),
    [alterarParcelas, pagamentos.condicaoNova]
  );
  const isRegraParcelasAtiva = useMemo(
    () => alterarParcelas && !!pagamentos.condicaoNova?.trim(),
    [alterarParcelas, pagamentos.condicaoNova]
  );
  const parcelasRulesValidation = useMemo(
    () =>
      validateAllParcelas({
        parcelas,
        parcelasBaseProtheus: parcelasBaseProtheus || {},
        parcelasValorBaseProtheus: parcelasValorBaseProtheus || {},
        parcelasValorBaseProtheusNormalizado:
          parcelasValorBaseProtheusNormalizado || {},
        parcelasValorBaseProtheusPorIndice:
          parcelasValorBaseProtheusPorIndice || [],
        isRegraAtiva: isRegraParcelasAtiva,
      }),
    [
      parcelas,
      parcelasBaseProtheus,
      parcelasValorBaseProtheus,
      parcelasValorBaseProtheusNormalizado,
      parcelasValorBaseProtheusPorIndice,
      isRegraParcelasAtiva,
    ]
  );
  const parcelasRulesPendentes = !parcelasRulesValidation.isValid;
  const totalParcelas = useMemo(
    () => parcelas.reduce((sum, p) => sum + (p.valor || 0), 0),
    [parcelas]
  );
  const isValorTotalValido = useMemo(
    () => valorNF === 0 || Math.abs(totalParcelas - valorNF) < 0.01,
    [totalParcelas, valorNF]
  );
  const parcelasTotalPendentes = isRegraParcelasAtiva && !isValorTotalValido;
  const parcelasTipoLivrePendentes = useMemo(() => {
    if (!isTipoLivre) return false;
    return !(
      parcelas.length > 0 && parcelas.every((p) => !!p.vencimento && p.valor > 0)
    );
  }, [isTipoLivre, parcelas]);
  const canCloseDialog =
    !condicaoNovaPendente &&
    !parcelasTipoLivrePendentes &&
    !parcelasRulesPendentes &&
    !parcelasTotalPendentes;

  const handleDialogOpenChange = (open: boolean) => {
    if (!open && !canCloseDialog) return;
    setIsDialogOpen(open);
  };
  const isInteractionInsideRulesCard = (target: EventTarget | null) => {
    if (!(target instanceof Element)) return false;
    return !!target.closest("[data-parcelas-rules-card='true']");
  };

  return (
    <div className="col-span-3 space-y-2 flex flex-col">
      <Dialog open={isDialogOpen} onOpenChange={handleDialogOpenChange}>
        <DialogTrigger asChild>
          <Button
            variant="secondary"
            className={cn(
              "flex justify-between border border-cyan-500 w-full overflow-hidden text-[9pt] bg-cyan-950/20",
              className
            )}
          >
            <span className="flex-1 min-w-0 text-start truncate text-cyan-500 font-medium">
              {btnLabel}
            </span>

            {isLoading && canFetchParcelas ? (
              <Loader className="size-5" />
            ) : (
              <Banknote className="size-5 text-cyan-500" />
            )}
          </Button>
        </DialogTrigger>
        <DialogContent
          className="min-h-[500px] flex flex-col items-center"
          showCloseButton={canCloseDialog}
          onInteractOutside={(e) => {
            if (!canCloseDialog || isInteractionInsideRulesCard(e.target)) {
              e.preventDefault();
            }
          }}
          onEscapeKeyDown={(e) => {
            if (!canCloseDialog) e.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Financeiro (Condição, PIX e Pagamento)</span>
            </DialogTitle>
          </DialogHeader>
          <Separator />

          {!canCloseDialog && (
            <div className="w-full text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-md p-3">
              {condicaoNovaPendente
                ? "Selecione a condição nova (tipo 9) antes de fechar."
                : parcelasRulesPendentes
                  ? "Existem regras de parcelas inválidas. Veja o modal de regras."
                  : parcelasTotalPendentes
                    ? "A soma das parcelas deve fechar exatamente o valor da nota."
                  : "Preencha o vencimento e o valor de todas as parcelas antes de fechar."}
            </div>
          )}

          <div className="flex flex-col mx-auto w-full gap-5 justify-start">
            <div className="col-span-1 sm:col-span-2 space-y-4">
              {temPedidoCompra && (
                <p className="text-xs text-muted-foreground">
                  Condição bloqueada: há pedido de compra vinculado.
                </p>
              )}
              {isTipoLivre && (
                <p className="text-xs text-blue-600 dark:text-blue-400">
                  Tipo livre — as parcelas podem ser editadas manualmente.
                </p>
              )}
              <Combobox
                options={condicoesFiltradas}
                value={pagamentos.condicaoFinanceira|| ""}
                onChange={handleCondicaoChange}
                placeholder="Selecione a condição"
                className="text-xs"
                disabled={temPedidoCompra}
              />
              {alterarParcelas && (
                <Combobox
                  options={condicoesTipo9Filtradas}
                  value={pagamentos.condicaoNova || ""}
                  onChange={handleCondicaoNovaChange}
                  placeholder="Selecione a condição nova (tipo 9)"
                  className="text-xs"
                />
              )}
            </div>

            <ParcelasTable
              alterarParcelas={alterarParcelas}
              onAlterarParcelasChange={handleAlterarParcelasChange}
              showAlterarParcelas={canShowAlterarParcelas}
            />
            <Separator />

            <PixSection />
          </div>
        </DialogContent>
      </Dialog>
      {hasMounted &&
        isDialogOpen &&
        isRegraParcelasAtiva &&
        createPortal(
          <div
            data-parcelas-rules-card="true"
            className="fixed top-25 z-[80] pointer-events-none sm:inset-x-35 sm:w-[360px]"
          >
            <div className="pointer-events-auto">
              <ParcelasRulesDialog
                open={false}
                onOpenChange={() => {}}
                validation={parcelasRulesValidation}
                isRegraAtiva={isRegraParcelasAtiva}
                parcelasBaseProtheus={parcelasBaseProtheus || {}}
                parcelasValorBaseProtheus={parcelasValorBaseProtheus || {}}
                inline
                className="max-h-[70vh] overflow-y-auto shadow-2xl"
              />
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
